// src/components/CounterSection.jsx
import React, { useEffect, useRef, useState } from "react";

function useInView(options = { threshold: 0.2 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.unobserve(el); // animate once
      }
    }, options);
    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);

  return [ref, inView];
}

function CountUp({ to, duration = 2000, inView, className, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let startTs = 0;
    let rafId;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / duration, 1);
      setVal(Math.round(p * Number(to)));
      if (p < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [inView, to, duration]);

  return <span className={className}>{prefix}{val}{suffix}</span>;
}

export default function CounterSection() {
  const [ref, inView] = useInView({ threshold: 0.25, rootMargin: "0px 0px -10% 0px" });

  const stats = [
    { label: "Budget-friendly", to: 100, suffix: "%", duration: 2000 },
    { label: "Homely and comfortable", to: 100, suffix: "%", duration: 2000 },
    { label: "Ensured Safety", to: 100, suffix: "%", duration: 2000 },
    { label: "Neat and Well-kept", to: 100, suffix: "%", duration: 2000 },
  ];

  return (
    <div className="counter-section">
      <div className="auto-container">
        <div className="sec-title centered">
          <h2>Interesting Facts</h2>
          <div className="sub-title">A summary of our challenges translated into numbers</div>
          <div className="separator">
            <span className="dott"></span>
            <span className="dott"></span>
            <span className="dott"></span>
          </div>
        </div>
      </div>

      <div className="fact-section" style={{ backgroundImage: "url('/images/background/2.jpg')" }}>
        <div className="auto-container">
          <div className="row clearfix" ref={ref}>
            {/* Title Column */}
            <div className="title-column col-md-6 col-sm-12 col-xs-12">
              <div className="title-inner">
                <div className="number">
                  <CountUp to={8} duration={1500} inView={inView} className="count-text" />
                </div>
                <div className="text">
                  Navigating the PG landscape with over seven years of expertise.
                </div>
              </div>
            </div>

            {/* Counter Column */}
            <div className="counter-column col-md-6 col-sm-12 col-xs-12">
              <div className="fact-counter">
                <div className="auto-container">
                  <div className="row clearfix">
                    {stats.map((s, i) => (
                      <div className="column counter-column col-md-6 col-sm-6 col-xs-12" key={i}>
                        <div className="inner">
                          <div className="count-outer count-box">
                            <CountUp
                              to={s.to}
                              duration={s.duration}
                              inView={inView}
                              className="count-text"
                              suffix={s.suffix}
                            />
                          </div>
                          <h4 className="counter-title">{s.label}</h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* /Counter Column */}
          </div>
        </div>
      </div>
    </div>
  );
}
